import React, { useState } from "react";
import { Loader2, ShieldAlert, Trash2 } from "lucide-react";
import { TID } from "../../constants/testIds";

/** Final confirmation modal — user must type RESET to execute. */
export const MassReplaceConfirmModal = ({ mode, preview, deleteMissing, onCancel, onExecute }) => {
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [executing, setExecuting] = useState(false);

  const submit = async () => {
    if (confirmPhrase !== "RESET") return;
    setExecuting(true);
    try {
      await onExecute();
    } catch {
      setConfirmPhrase("");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
      <div className="w-full max-w-lg rounded-lg border-2 border-red-300 bg-white shadow-2xl">
        <div className="flex items-start gap-3 border-b border-slate-200 p-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
            <ShieldAlert size={20} className="text-red-700" />
          </div>
          <div>
            <div className="font-heading text-lg font-bold text-slate-900">
              Confirm mass replacement
            </div>
            <div className="text-xs text-slate-600 mt-1">
              You are about to run <b>{mode === "overwrite" ? "Ignore all & overwrite" : "Cross-reference"}</b>.
              {mode === "overwrite" && (
                <span className="block mt-1 text-red-700">
                  This will WIPE the facilities collection and insert {preview?.counts.new_file_rows || 0} rows from the file.
                </span>
              )}
              {mode === "cross_reference" && (
                <span className="block mt-1">
                  Adds: <b>{preview?.counts.adds || 0}</b>, Updates: <b>{preview?.counts.updates || 0}</b>,
                  {" "}{deleteMissing ? "Hard-delete" : "Flag as missing"}: <b>{preview?.counts.removes || 0}</b>
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-slate-700">
            Type <span className="rounded bg-red-100 px-1.5 py-0.5 font-mono font-bold text-red-800">RESET</span> below to confirm.
            A backup snapshot will be saved automatically.
          </div>
          <input
            data-testid={TID.massReplaceConfirmInput}
            autoFocus
            type="text"
            value={confirmPhrase}
            onChange={(e) => setConfirmPhrase(e.target.value)}
            placeholder="Type RESET"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:border-red-500 focus:outline-none"
          />
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 p-4">
          <button
            data-testid={TID.massReplaceCancel}
            onClick={onCancel}
            disabled={executing}
            className="rounded-md border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid={TID.massReplaceExecute}
            onClick={submit}
            disabled={confirmPhrase !== "RESET" || executing}
            className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
          >
            {executing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} strokeWidth={2.5} />}
            Execute replacement
          </button>
        </div>
      </div>
    </div>
  );
};
